import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getRandomWord } from '../../data/jewishWords';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faVolumeHigh, faDice } from '@fortawesome/free-solid-svg-icons';
import BottomSheet from '../BottomSheet';

export default function WordModal({ isOpen, onClose, wordOfTheDay }) {
  const [randomWord, setRandomWord] = useState(null);

  // Reset random word when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRandomWord(null);
    }
  }, [isOpen]);

  const handleRandomWord = () => {
    setRandomWord(getRandomWord());
  };

  // Use wordOfTheDay as the main word, or randomWord if user clicked random
  const currentWord = randomWord || wordOfTheDay;

  const handlePronounce = () => {
    if (currentWord && 'speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(currentWord.transliteration);
      // Use appropriate language based on word language
      utterance.lang = currentWord.language === 'Yiddish' ? 'yi' : 'he-IL';
      utterance.rate = 0.8; // Slightly slower for clarity
      window.speechSynthesis.speak(utterance);
    }
  };

  if (!isOpen || !wordOfTheDay) return null;

  // Content component to be used in both BottomSheet and regular modal
  const ModalContent = () => (
    <div className="p-4 md:p-6">
      {/* Word Content */}
      <div className="text-center mb-6">
        {/* Word Text */}
        <div className="mb-4">
          <div className="text-4xl sm:text-5xl md:text-6xl font-serif text-text-primary mb-2" dir="rtl">
            {currentWord.hebrew}
          </div>
          <div className="text-xl sm:text-2xl md:text-3xl font-semibold text-primary mb-2">
            {currentWord.transliteration}
          </div>
          {/* Language Badge */}
          <div className="inline-block bg-primary/20 text-primary text-xs font-semibold px-3 py-1 rounded-full mt-2">
            {currentWord.language || 'Hebrew'}
          </div>
        </div>

        {/* Meaning */}
        <div className="mb-4">
          <div className="text-sm text-text-secondary mb-1">Meaning</div>
          <div className="text-base md:text-lg font-semibold text-text-primary">
            {currentWord.meaning}
          </div>
        </div>

        {/* Example */}
        <div className="bg-surface rounded-xl p-4 mb-4">
          <div className="text-sm text-text-secondary mb-1">Example</div>
          <div className="text-sm md:text-base text-text-primary italic">
            "{currentWord.example}"
          </div>
        </div>

        {/* Category */}
        <div className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">
          {currentWord.category}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={handlePronounce}
          className="flex-1 px-4 py-3 bg-surface hover:bg-[#F0F0F0] rounded-xl font-semibold text-text-primary transition-colors flex items-center justify-center gap-2 touch-target active:scale-95"
        >
          <FontAwesomeIcon icon={faVolumeHigh} />
          <span>Pronounce</span>
        </button>
        <button
          onClick={handleRandomWord}
          className="flex-1 px-4 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 touch-target active:scale-95"
        >
          <FontAwesomeIcon icon={faDice} />
          <span>Random Word</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile: BottomSheet */}
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title="Word of the Day"
        fullScreen={false}
      >
        <ModalContent />
      </BottomSheet>

      {/* Desktop: Regular Modal (hidden on mobile) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="hidden md:flex fixed inset-0 bg-black/50 z-50 items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h2 className="text-xl sm:text-2xl font-semibold text-text-primary">
                  Word of the Day
                </h2>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors touch-target"
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <ModalContent />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

